import React, { useEffect, useState, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { ErrorMessage } from '@hookform/error-message';
import { Row, Form, Col, Input } from 'reactstrap';
import { toast } from 'react-toastify';
import Select from 'react-select/async';

import CatalogProductsForm from '../../../PriceCatalog/externalForm/CatalogProductsForm';
import CustomFieldsInputsForm from '../../../shared/components/CustomFieldsInputsForm';
import RecordTypeService from '../../../../RecordType/service';
import ReactButton from 'components/Buttons/ReactButton';
import FlexSelect from 'components/Inputs/FlexSelect';
import LeadService from '../../services/leadService';
import ReactLoader from 'components/Loader';
import { FlexController } from 'components/FlexControllerInput';
import { getDefaultCountry } from 'services/ServiceCOUNTRYS';

import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import { useListLeadStagesByRecordType } from '../../../LeadStages/hooks/useListLeadStagesByRecordType';
import { formatCustomFieldsToSubmit } from 'views/CRM/shared/helpers/customFieldsHelpers';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import { removeFields } from 'utils/fuctions/removeObjectsFields';
import { useSearchUsers } from 'views/User/hooks/useSearchUsers';
import { custom_select } from 'assets/styles/multiple_select';
import { fieldsToRemove } from 'views/CRM/PriceCatalog/utils';
import { ScreenPrompt } from 'components/ScreenPrompt';
import { AiOutlineClear } from 'react-icons/ai';
import { normalizePhoneNumber } from 'utils/fuctions/internationalPhoneFormatter';
import { normalizeDDI } from 'utils/fuctions/internationalPhoneFormatter';
import { verifyRoles } from 'components/Authentication/VerifyRoles';

const emailRegex =
  // eslint-disable-next-line no-useless-escape
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;

const LeadInputs = ({
  recordTypeId,
  isModal = false,
  callbackFunc = () => {},
  conversation = {}
}) => {
  const history = useHistory();
  const {
    control,
    handleSubmit,
    errors,
    setValue,
    register,
    setError,
    formState,
    reset,
    watch,
    getValues
  } = useForm({
    defaultValues: {
      productItems: []
    }
  });

  const { isDirty } = formState;
  const [submitAttempt, setSubmitAttempt] = useState(false);

  const catalogFormRef = useRef(null);

  const [isSubmiting, setIsSubmiting] = useState(false);

  const [recordType, setRecordType] = useState(null);

  const [responsible, setResponsible] = useState({
    label: localStorage.getItem('name'),
    value: localStorage.getItem('login')
  });

  const submittedSuccessfully = useRef(false);

  useEffect(() => {
    const lead = localStorage.getItem('leadValue');
    if (lead) {
      reset(JSON.parse(lead));
    }
  }, []);

  const { isSearchingUsers, searchUsers } = useSearchUsers();

  const { getCustomFields, customFields } = useEntityCustomFieldsByRecordType(
    entitiesName.LEAD,
    recordTypeId
  );

  const { getLeadStagesByRecordType, isLoadingLeadStages, leadStages } =
    useListLeadStagesByRecordType();

  useEffect(() => {
    getCustomFields();
  }, [getCustomFields]);

  useEffect(() => {
    return () => {
      if (!submittedSuccessfully.current && isDirty) {
        const currentValues = getValues();
        const cleanedData = removeFields(currentValues, fieldsToRemove);
        localStorage.setItem('leadValue', JSON.stringify(cleanedData));
      }
    };
  }, [isDirty, getValues]);

  useEffect(() => {
    if (recordTypeId) {
      RecordTypeService.get(recordTypeId)
        .then((res) => {
          const recordTypeData = res.data.content.disable_link_to_product;
          setRecordType(recordTypeData);
        })
        .catch((error) => {
          const errorMsg = error.response?.data?.message;
          toast.error(errorMsg || 'Erro ao buscar tipo de registro');
        });
    }
  }, [recordTypeId]);

  useEffect(() => {
    if (conversation?.cliente) {
      reset({
        name: conversation.cliente.nome || '',
        email: conversation.cliente.email || '',
        phone: {
          number: conversation.cliente.telefone || '',
          ddi: conversation.cliente.ddi || ''
        }
      });
    }
  }, [conversation, reset]);

  useEffect(() => {
    getLeadStagesByRecordType(recordTypeId);
  }, [recordTypeId, getLeadStagesByRecordType]);

  useEffect(() => {
    register('responsibleLogin');
  }, [register]);

  const removeMaskSpecialCharacters = (phone) => {
    const regexToRemoveSpecialCharacters = /[^0-9a-zA-Z]+/g;
    return phone?.replace?.(regexToRemoveSpecialCharacters, '');
  };

  const onSubmit = async (data) => {
    setSubmitAttempt(true);

    const originalData = { ...data };
    if (!responsible || !responsible.value) {
      setError('responsibleLogin', { message: 'Campo obrigatório' });
      return;
    }

    setIsSubmiting(true);
    const formattedData = {
      ...data,
      phone: removeMaskSpecialCharacters(data.phone),
      responsibleLogin: responsible.value,
      defaultContactRecordTypeId: 301
    };
    let dataToSubmit = formattedData;
    if (!recordType && data.catalogId && catalogFormRef.current) {
      dataToSubmit = catalogFormRef.current.formatProductsAndCatalogsToShow({
        ...formattedData
      });
    }

    const dataDTO = formatCustomFieldsToSubmit(dataToSubmit, customFields);

    dataDTO.phone = normalizePhoneNumber(
      data?.phone?.number || data?.__number__
    );
    dataDTO.ddi = normalizeDDI(
      data?.phone?.ddi ||
        data?.__ddi__ ||
        getDefaultCountry(dataDTO?.phone)?.dialCode
    );

    try {
      const saveLeadPromise = await LeadService.createLead(
        recordTypeId,
        dataDTO
      );
      const message = saveLeadPromise.data.message;
      toast.success(message ?? 'Lead criado com sucesso');

      submittedSuccessfully.current = true;
      localStorage.removeItem('leadValue');

      reset({ productItems: [] });
      setResponsible({
        label: localStorage.getItem('name'),
        value: localStorage.getItem('login')
      });

      if (!isModal && verifyRoles(['CRM_LEADS_EDIT'])) {
        history.push(
          `/admin/crm/leadEdit/${recordTypeId}/${saveLeadPromise?.data?.data?.id}`
        );
      } else if (!verifyRoles(['CRM_LEADS_EDIT'])) {
        history.push(`/admin/crm/lead`);
      } else {
        callbackFunc();
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || 'Erro ao criar o lead',
        {
          autoClose: 3000,
          closeOnClick: true
        }
      );

      const cleanedData = removeFields(originalData, fieldsToRemove);
      localStorage.setItem('leadValue', JSON.stringify(cleanedData));
    } finally {
      setIsSubmiting(false);
    }
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const cancelAction = () => {
    if (isModal) {
      callbackFunc();
      return;
    }

    history.push('/admin/crm/lead', { recordTypeId });
  };

  const defaultStage = leadStages?.filter(
    (leadStage) => leadStage.isDefault
  )[0];

  if (isLoadingLeadStages) {
    return (
      <Row className="justify-content-md-center">
        <ReactLoader />
      </Row>
    );
  }

  return (
    <>
      <ScreenPrompt
        when={isDirty && !formState.isSubmitting}
        message={`Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar`}
      />
      <Form className="needs-validation" onSubmit={handleSubmit(onSubmit)}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            style={{
              display: isModal ? 'block' : 'none',
              border: 'none',
              background: 'none'
            }}
            onClick={() => {
              localStorage.removeItem('leadValue');
              reset({ stageId: null });
              setValue('responsibleLogin', null);
              setResponsible(null);
            }}
            id="buttonInChild"
          >
            <span
              onClick={() => {
                document.getElementById('buttonInChild').click();
              }}
              className="small"
              style={{ cursor: 'pointer' }}
            >
              <AiOutlineClear /> Limpar campos
            </span>
          </button>
        </div>
        <Row className="mb-3">
          <Col sm="6">
            <label className="form-control-label">Nome*</label>
            <Controller
              render={(props) => (
                <Input
                  value={props.value}
                  onChange={props.onChange}
                  type="text"
                  placeholder="Nome"
                />
              )}
              control={control}
              defaultValue={conversation?.cliente?.nome || ''}
              rules={{ required: 'Campo obrigatório' }}
              name="name"
            />
            <ErrorMessage
              errors={errors}
              name="name"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
          <Col sm="6">
            <label className="form-control-label">Email</label>
            <Controller
              render={(props) => (
                <Input
                  value={props.value}
                  onChange={props.onChange}
                  type="text"
                  placeholder="Email"
                />
              )}
              control={control}
              defaultValue={conversation?.cliente?.email || ''}
              rules={{
                validate: (value) => {
                  if (!value) return true;
                  return (
                    emailRegex.test(value) || 'Email com formato incorreto'
                  );
                }
              }}
              name="email"
            />
            <ErrorMessage
              errors={errors}
              name="email"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col sm="12" md="6">
            <FlexController
              type="phone"
              label="Telefone"
              name="phone"
              watch={watch}
              control={control}
              errors={errors}
              valueController={(fieldName, value) => {
                setValue(fieldName, value);
              }}
              rules={{ required: false }}
              validateOnSubmit={submitAttempt}
              disabled={false}
            />
          </Col>
          <Col sm="12" md="6">
            <label className="form-control-label">Fase do lead*</label>
            <Controller
              render={(props) => (
                <FlexSelect
                  dataOptions={leadStages || []}
                  isMulti={false}
                  value={props.value}
                  valueController={setValue}
                  fieldName="stageId"
                  labelName="name"
                  valueName="id"
                  isClearable={true}
                  disabled={isLoadingLeadStages}
                  closeMenuOnSelect={true}
                />
              )}
              control={control}
              defaultValue={defaultStage?.id || ''}
              rules={{
                required: 'Campo obrigatório'
              }}
              name="stageId"
            />
            <ErrorMessage
              errors={errors}
              name="stageId"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md="6">
            <div className="d-flex">
              <div style={{ flexGrow: '10' }}>
                <label className="form-control-label">Responsável*</label>
                <Select
                  styles={custom_select}
                  placeholder="Busca rápida"
                  isClearable={true}
                  name="responsibleLogin"
                  isLoading={isSearchingUsers}
                  loadingMessage={() => 'Buscando...'}
                  noOptionsMessage={({ inputValue }) =>
                    !inputValue
                      ? 'Digite para pesquisar'
                      : 'Nenhum resultado encontrado'
                  }
                  loadOptions={async (inputValue) => {
                    const data = await searchUsers(inputValue);
                    return (
                      data &&
                      data.map((user) => ({
                        label: user.name,
                        value: user.login
                      }))
                    );
                  }}
                  onChange={(data) => {
                    if (data) {
                      const { value, label } = data;
                      setResponsible({
                        label,
                        value
                      });
                    } else {
                      setResponsible(null);
                    }
                  }}
                  value={
                    responsible
                      ? {
                          label: responsible.label,
                          value: responsible.value
                        }
                      : ''
                  }
                  defaultValue={
                    responsible
                      ? {
                          label: responsible.label,
                          value: responsible.value
                        }
                      : ''
                  }
                />
                <ErrorMessage
                  errors={errors}
                  name="responsibleLogin"
                  render={({ message }) => errorFormMessage(message)}
                />
              </div>
            </div>
          </Col>

          <CustomFieldsInputsForm
            fields={customFields}
            control={control}
            errors={errors}
            setValue={setValue}
            Controller={Controller}
            watch={watch}
          />
        </Row>
        {!recordType && (
          <CatalogProductsForm
            ref={catalogFormRef}
            watch={watch}
            getValues={getValues}
            control={control}
            setValue={setValue}
          />
        )}

        <ReactButton
          btnColor="confirmation"
          type="submit"
          disabled={isSubmiting}
        >
          Salvar
        </ReactButton>

        <ReactButton
          btnColor="cancelation"
          onClick={() => {
            return cancelAction();
          }}
        >
          Cancelar
        </ReactButton>
      </Form>
    </>
  );
};

export default LeadInputs;
